import AssignableAgentsAPI from '../../api/assignableAgents';

// AbortController para cancelar requisições pendentes
let currentAbortController = null;

// Cache para evitar requisições duplicadas
const requestCache = new Map();
const CACHE_TTL = 10000; // 10 segundos

// Debounce timer
let debounceTimer = null;
const DEBOUNCE_DELAY = 100; // 100ms

const state = {
  records: {},
  uiFlags: {
    isFetching: false,
  },
};

export const types = {
  SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG: 'SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG',
  SET_INBOX_ASSIGNABLE_AGENTS: 'SET_INBOX_ASSIGNABLE_AGENTS',
};

export const getters = {
  getAssignableAgents: $state => inboxId => {
    const allAgents = $state.records[inboxId] || [];
    const verifiedAgents = allAgents.filter(record => record.confirmed);
    return verifiedAgents;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

// Helper para verificar se erro é de cancelamento
const isCanceledError = error => {
  return (
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message?.includes('canceled') ||
    error?.message?.includes('aborted')
  );
};

// Helper para verificar cache
const getCachedData = key => {
  const cached = requestCache.get(key);
  if (!cached) return null;

  const isExpired = Date.now() - cached.timestamp > CACHE_TTL;
  if (isExpired) {
    requestCache.delete(key);
    return null;
  }

  return cached.data;
};

export const actions = {
  fetch({ commit, state: $state }, inboxIds) {
    // Validar entrada
    if (!inboxIds || !Array.isArray(inboxIds) || inboxIds.length === 0) {
      return Promise.resolve(null);
    }

    const cacheKey = inboxIds.sort().join(',');

    // Verificar cache primeiro
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      commit(types.SET_INBOX_ASSIGNABLE_AGENTS, {
        inboxId: cacheKey,
        members: cachedData,
      });
      return Promise.resolve(cachedData);
    }

    // Se já está buscando os mesmos dados, ignorar
    if ($state.uiFlags.isFetching) {
      return Promise.resolve(null);
    }

    // Debounce para evitar múltiplas chamadas rápidas
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    return new Promise(resolve => {
      debounceTimer = setTimeout(async () => {
        // Cancelar requisição anterior se existir
        if (currentAbortController) {
          currentAbortController.abort();
        }

        // Criar novo AbortController
        currentAbortController = new AbortController();
        const { signal } = currentAbortController;

        commit(types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG, { isFetching: true });

        try {
          const {
            data: { payload },
          } = await AssignableAgentsAPI.get(inboxIds, { signal });

          // Armazenar no cache
          requestCache.set(cacheKey, {
            timestamp: Date.now(),
            data: payload,
          });

          commit(types.SET_INBOX_ASSIGNABLE_AGENTS, {
            inboxId: cacheKey,
            members: payload,
          });

          resolve(payload);
        } catch (error) {
          // Silenciar erros de cancelamento - são esperados
          if (isCanceledError(error)) {
            resolve(null);
            return;
          }
          // Outros erros - logar mas não relançar para não quebrar UI
          // eslint-disable-next-line no-console
          console.warn('Error fetching assignable agents:', error);
          resolve(null);
        } finally {
          commit(types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG, {
            isFetching: false,
          });
          currentAbortController = null;
        }
      }, DEBOUNCE_DELAY);
    });
  },
};

export const mutations = {
  [types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_INBOX_ASSIGNABLE_AGENTS]: ($state, { inboxId, members }) => {
    $state.records = {
      ...$state.records,
      [inboxId]: members,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
